import { RevealBox, SectionLabel, SectionTitle, SectionSub } from './UI';
// At top of Sections.js
import VideoTestimonials from './VideoTestimonials';

// ── WHO CAN REGISTER ────────────────────────────────────────────────────────
const whoCards = [
  { icon: '💼', title: 'Working Professionals', desc: 'Upskill without quitting your job. Earn a degree on your own schedule.' },
  { icon: '🎓', title: 'Fresh Graduates', desc: 'Boost your profile and employability with a recognised postgraduate degree.' },
  { icon: '🏠', title: 'Homemakers', desc: 'Restart your career journey from home, at your own comfortable pace.' },
  { icon: '🚀', title: 'Entrepreneurs', desc: 'Add academic credibility and structured business knowledge to your venture.' },
  { icon: '🔄', title: 'Career Changers', desc: 'Pivot industries with the right credentials, mentorship, and university backing.' },
  { icon: '📚', title: 'Lifelong Learners', desc: 'Keep growing intellectually and professionally — there\'s no age limit here.' },
];

export function WhoCanRegister() {
  return (
    <section id="who" style={{ padding: '80px 5%', background: 'var(--white)' }}>
      <RevealBox style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
        <SectionLabel>Who We Serve</SectionLabel>
        <SectionTitle style={{ maxWidth: '100%', textAlign: 'center' }}>
          Education for <em style={{ fontStyle: 'italic' }}>Everyone</em>
        </SectionTitle>
        <SectionSub style={{ maxWidth: '100%', textAlign: 'center' }}>
          Whether you're starting out, levelling up, or starting fresh — iGrade has the right path for you.
        </SectionSub>
      </RevealBox>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px,1fr))',
        gap: 12, marginTop: 40,
      }}>
        {whoCards.map((c, i) => (
          <RevealBox key={c.title} delay={i * 0.06}>
            <div style={{
              background: 'var(--white)', border: '1.5px solid var(--graylt)',
              borderRadius: 'var(--radius)', padding: '20px 14px', textAlign: 'center',
              transition: 'all 0.3s', cursor: 'default',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--yellow3)'; e.currentTarget.style.borderColor = 'var(--yellow)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--white)'; e.currentTarget.style.borderColor = 'var(--graylt)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <div style={{ fontSize: 24, width: 48, height: 48, borderRadius: '50%', background: 'var(--yellow3)', margin: '0 auto 12px', display: 'grid', placeItems: 'center' }}>{c.icon}</div>
              <h4 style={{ fontFamily: 'DM Sans,sans-serif', fontWeight: 700, fontSize: 14, color: 'var(--navy)', marginBottom: 4 }}>{c.title}</h4>
              <p style={{ fontSize: 12, color: 'var(--gray)', lineHeight: 1.5 }}>{c.desc}</p>
            </div>
          </RevealBox>
        ))}
      </div>
    </section>
  );
}

// ── PROCESS STEPS ────────────────────────────────────────────────────────────
const steps = [
  { num: '01', title: 'Share Your Goals', desc: 'Fill a quick form — tell us your background, career goals, and timeline.' },
  { num: '02', title: 'Expert Counselling', desc: 'A dedicated advisor calls within 24 hrs to recommend the perfect program.' },
  { num: '03', title: 'We Handle Docs', desc: 'Submit documents once. We handle eligibility, forms, and university submission.' },
  { num: '04', title: 'Begin Learning', desc: 'Receive enrollment confirmation and start your online degree journey.' },
];

export function Process() {
  return (
    <section id="process" style={{ padding: '80px 5%', background: 'var(--offwhite)' }}>
      <RevealBox style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
        <SectionLabel>How It Works</SectionLabel>
        <SectionTitle style={{ maxWidth: '100%', textAlign: 'center' }}>
          Enrolled in <em style={{ fontStyle: 'italic' }}>4 Simple Steps</em>
        </SectionTitle>
        <SectionSub style={{ maxWidth: '100%', textAlign: 'center' }}>
          From curious visitor to official university student in record time.
        </SectionSub>
      </RevealBox>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px,1fr))',
        gap: 24, marginTop: 48,
      }}>
        {steps.map((s, i) => (
          <RevealBox key={s.num} delay={i * 0.1}>
            <div style={{ textAlign: 'center', padding: '0 8px' }}>
              <div style={{
                width: 64, height: 64, borderRadius: '50%',
                background: 'var(--navy)', color: 'var(--yellow)',
                fontFamily: 'Playfair Display,serif', fontSize: 22, fontWeight: 900,
                display: 'grid', placeItems: 'center', margin: '0 auto 16px',
                boxShadow: '0 0 0 6px var(--offwhite), 0 0 0 8px var(--navy)',
              }}>{s.num}</div>
              <h4 style={{ fontFamily: 'DM Sans,sans-serif', fontWeight: 700, fontSize: 15, color: 'var(--navy)', marginBottom: 8 }}>{s.title}</h4>
              <p style={{ fontSize: 13, color: 'var(--gray)', lineHeight: 1.6 }}>{s.desc}</p>
            </div>
          </RevealBox>
        ))}
      </div>
    </section>
  );
}

// ── THREE PILLARS ────────────────────────────────────────────────────────────
const pillars = [
  {
    label: 'Pillar 01 — Brand Identity', bg: 'var(--white)',
    title: <>A Tier-1 Institution, <em style={{ fontStyle: 'italic', color: 'var(--navy2)' }}>Presented With Excellence</em></>,
    sub: 'iGrade is not just a registration platform — it\'s a premium brand built on trust, authority, and visual excellence.',
    bullets: [
      { icon: '✦', title: 'High-End Visual Identity', desc: 'Apple-inspired design — glassmorphism, clean typography, sophisticated palette — signals quality at a glance.' },
      { icon: '★', title: 'Unified Value Proposition', desc: 'Our platform communicates iGrade\'s unique market edge within the first 3 seconds of every visit.' },
      { icon: '🏅', title: 'Digital Brand Consistency', desc: 'Every pixel reflects a "Tier-1" educational institution — custom-engineered feel, not template looks.' },
    ],
    visual: [
      { icon: '★', title: 'Premium Corporate Positioning', desc: 'Recognised as India\'s most trusted online education partner by 15,000+ students.' },
      { icon: '🏆', title: 'Award-Winning Platform', desc: 'Multiple industry recognitions for EdTech excellence and student satisfaction.' },
      { icon: '✦', title: 'Institutional Backing', desc: 'Formally partnered with NAAC A++ universities and UGC-accredited institutions.' },
    ],
    tag: 'Our Identity',
  },
  {
    label: 'Pillar 02 — Trust Engine', bg: 'var(--offwhite)',
    title: <>The "Trust Engine" That <em style={{ fontStyle: 'italic', color: 'var(--navy2)' }}>Silences Skeptics</em></>,
    sub: 'We systematically showcase our track record — because trust is earned through evidence, not promises.',
    bullets: [
      { icon: '📹', title: 'High-Impact Video Testimonials', desc: 'Dedicated sections for cinematic student and parent success stories that build emotional resonance.' },
      { icon: '🏅', title: 'Authority Signal Integration', desc: '"Wall of Fame" featuring industry awards, government recognitions, and verified Google/Trustpilot ratings.' },
      { icon: '🤝', title: 'Strategic Partnerships', desc: 'A visual grid showcasing corporate tie-ups and institutional affiliations validating our scale.' },
    ],
    visual: [
      { icon: '⭐', title: '400+ Verified Reviews', desc: 'Every testimonial is verified and showcases tangible, real career improvements.' },
      { icon: '🏛️', title: 'Government Recognition', desc: 'Formally acknowledged by India\'s top educational regulatory bodies.' },
      { icon: '📊', title: '4.9★ Verified Rating', desc: 'Consistently rated among the top EdTech platforms on Google & Trustpilot.' },
    ],
  tag: 'Trust Engine',
  
  },
{
  label: 'Pillar 03 — Conversion UX', bg: 'var(--white)',
    title: <>Zero Friction from <em style={{ fontStyle: 'italic', color: 'var(--navy2)' }}>Curiosity to Enrolment</em></>,
      sub: 'Our platform removes every barrier between a visitor\'s first question and their admission confirmation.',
        bullets: [
          { icon: '🎯', title: 'Smart CTA Placement', desc: 'Strategically deployed "Action Triggers" that follow natural scrolling heat-maps for maximum conversion.' },
          { icon: '📅', title: 'Virtual Consultation Integration', desc: 'Seamlessly embedded 1-on-1 scheduling tools catering to the busy modern professional.' },
          { icon: '📝', title: 'Frictionless Lead Capture', desc: 'Multi-step "smart" contact forms designed to maximise completion without feeling intrusive.' },
        ],
          visual: [
            { icon: '💬', title: 'Live WhatsApp Support', desc: 'Instant query resolution via embedded WhatsApp and chat integration.' },
            { icon: '📱', title: 'Mobile-First Design', desc: '73% of our users visit via mobile — seamless on every device.' },
            { icon: '⚙️', title: 'Smart Adaptive Forms', desc: 'Forms that personalise based on user profile and declared course interest.' },
          ],
            tag: 'UX Design',
  },
];

export function Pillars() {
  return (
    <>
      {pillars.map((p, pi) => (
        <section key={pi} style={{ padding: '80px 5%', background: p.bg }}>

          {/* Mobile: single column | Desktop: two columns */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 40,
          }}
            className={`pillar-grid pillar-grid-${pi}`}
          >
            <RevealBox>
              <SectionLabel>{p.label}</SectionLabel>
              <SectionTitle>{p.title}</SectionTitle>
              <SectionSub>{p.sub}</SectionSub>
              <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 16 }}>
                {p.bullets.map(b => (
                  <div key={b.title} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{
                      width: 28, height: 28, minWidth: 28, borderRadius: '50%',
                      background: 'var(--yellow3)', display: 'grid', placeItems: 'center',
                      fontSize: 13, color: 'var(--navy)', marginTop: 2,
                    }}>{b.icon}</div>
                    <div>
                      <strong style={{ display: 'block', fontSize: 14, color: 'var(--navy)', fontFamily: 'DM Sans,sans-serif', marginBottom: 3 }}>{b.title}</strong>
                      <p style={{ fontSize: 13, color: 'var(--gray)', lineHeight: 1.6 }}>{b.desc}</p>
                    </div>
                  </div>
                ))}
                {pi === 1 && <VideoTestimonials />}
              </div>
            </RevealBox>

            <RevealBox delay={0.15}>
              <div style={{
                background: 'var(--navy)', borderRadius: 16, padding: '28px 24px',
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{
                  position: 'absolute', inset: 0, pointerEvents: 'none',
                  background: 'radial-gradient(ellipse at 30% 30%, rgba(245,197,24,0.12) 0%, transparent 60%)',
                }} />
                <span style={{
                  display: 'inline-block', marginBottom: 20,
                  background: 'var(--yellow)', color: 'var(--navy)',
                  fontSize: 10, fontWeight: 800, padding: '4px 12px',
                  borderRadius: 999, letterSpacing: '1px',
                }}>{p.tag}</span>
                <div style={{ position: 'relative', zIndex: 1 }}>
                  {p.visual.map((v, vi) => (
                    <div key={v.title} style={{
                      display: 'flex', gap: 12, alignItems: 'flex-start',
                      padding: '14px 0',
                      borderBottom: vi < p.visual.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                    }}>
                      <div style={{
                        width: 28, height: 28, minWidth: 28, borderRadius: '50%',
                        background: 'rgba(245,197,24,0.12)', border: '1px solid rgba(245,197,24,0.3)',
                        display: 'grid', placeItems: 'center', fontSize: 13, color: 'var(--yellow)', marginTop: 2,
                      }}>{v.icon}</div>
                      <div>
                        <strong style={{ display: 'block', fontSize: 14, color: '#fff', fontFamily: 'DM Sans,sans-serif', marginBottom: 3 }}>{v.title}</strong>
                        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>{v.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </RevealBox>
          </div>

          <style>{`
            @media(min-width:860px){
              .pillar-grid { grid-template-columns: 1fr 1fr !important; gap: 72px !important; }
            }
          `}</style>
        </section>
      ))}
    </>
  );
}